import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Search, CheckCircle, XCircle, Users, Ticket, Loader, QrCode, ChevronLeft, AlertCircle, Camera, CameraOff } from "lucide-react";
import { getEventAttendees, checkInTicket } from "../../services/TicketingService";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseFunctions/config";
import jsQR from "jsqr";

const CheckIn = () => {
  const { id: eventId } = useParams();
  const [eventName, setEventName] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [qrInput, setQrInput] = useState("");
  const [activeTab, setActiveTab] = useState("list"); // "list" | "scanner"
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  // Camera state
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [lastScanned, setLastScanned] = useState(null); // debounce duplicate scans

  const qrInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Auto-focus QR input when on scanner tab (USB mode)
  useEffect(() => {
    if (activeTab === "scanner" && !cameraActive && qrInputRef.current) {
      qrInputRef.current.focus();
    }
  }, [activeTab, cameraActive]);

  // Stop camera when leaving scanner tab
  useEffect(() => {
    if (activeTab !== "scanner") stopCamera();
  }, [activeTab]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopCamera();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [eventSnap, attendeesResult] = await Promise.all([
        getDoc(doc(db, "events", eventId)),
        getEventAttendees(eventId),
      ]);
      if (eventSnap.exists()) setEventName(eventSnap.data().eventName || "");
      if (attendeesResult.success) setAttendees(attendeesResult.attendees || []);
    } catch (err) {
      console.error("CheckIn load error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckIn = async (purchaseId) => {
    if (scanning) return;
    setScanning(true);
    setScanResult(null);
    const result = await checkInTicket({ purchaseId, eventId });

    if (result.alreadyCheckedIn) {
      setScanResult({ status: "already", name: result.buyerName || "Guest", message: "Already checked in" });
    } else if (result.success) {
      setScanResult({ status: "success", name: result.buyerName || "Guest", ticketType: result.ticketTypeName });
      setAttendees(prev => prev.map(a => a.id === purchaseId ? { ...a, checkedIn: true } : a));
    } else {
      setScanResult({ status: "error", message: result.error || "Invalid ticket" });
    }
    setScanning(false);
    setQrInput("");
    if (qrInputRef.current && !cameraActive) qrInputRef.current.focus();
  };

  const handleQrSubmit = (e) => {
    e.preventDefault();
    const id = qrInput.trim();
    if (!id) return;
    handleCheckIn(id);
  };

  const handleListCheckIn = async (purchaseId) => {
    await handleCheckIn(purchaseId);
  };

  // ── Camera scanning ───────────────────────────────────────────────────────

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }, // rear camera on mobile
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCameraActive(true);
      scanFrame();
    } catch (err) {
      setCameraError("Camera access denied. Use USB scanner or guest list.");
    }
  };

  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const scanFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);

    if (code && code.data && code.data !== lastScanned) {
      setLastScanned(code.data);
      handleCheckIn(code.data);
      // Reset lastScanned after 3s to allow re-scan
      setTimeout(() => setLastScanned(null), 3000);
    }

    rafRef.current = requestAnimationFrame(scanFrame);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastScanned]);

  // Restart scan loop when cameraActive changes
  useEffect(() => {
    if (cameraActive) {
      rafRef.current = requestAnimationFrame(scanFrame);
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [cameraActive, scanFrame]);

  // ─────────────────────────────────────────────────────────────────────────

  const filtered = attendees.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.buyerName?.toLowerCase().includes(q) ||
      a.buyerEmail?.toLowerCase().includes(q) ||
      a.ticketTypeName?.toLowerCase().includes(q)
    );
  });

  const checkedInCount = attendees.filter(a => a.checkedIn).length;

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-grey-50">
      <Loader className="w-8 h-8 text-grey-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      <div className="bg-white border-b border-grey-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={`/eo/events/${eventId}`} className="p-1.5 hover:bg-grey-100 rounded-lg transition">
              <ChevronLeft className="w-5 h-5 text-grey-600" />
            </Link>
            <div>
              <h1 className="text-base font-semibold text-grey-900">Check-In</h1>
              <p className="text-xs text-grey-500">{eventName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-grey-50 border border-grey-200 rounded-xl px-3 py-1.5">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm font-semibold text-grey-900">{checkedInCount}</span>
            <span className="text-sm text-grey-400">/</span>
            <span className="text-sm text-grey-600">{attendees.length}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-2xl mx-auto px-4 pb-3 flex gap-1">
          <button
            onClick={() => setActiveTab("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${activeTab === "list" ? "bg-grey-900 text-white" : "text-grey-500 hover:bg-grey-100"}`}
          >
            <Users className="w-3.5 h-3.5" />
            Guest List
          </button>
          <button
            onClick={() => setActiveTab("scanner")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${activeTab === "scanner" ? "bg-grey-900 text-white" : "text-grey-500 hover:bg-grey-100"}`}
          >
            <QrCode className="w-3.5 h-3.5" />
            Scan QR
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">

        {/* Scan result toast */}
        {scanResult && (
          <div className={`mb-4 p-4 rounded-2xl flex items-center gap-3 ${
            scanResult.status === "success" ? "bg-green-50 border border-green-200" :
            scanResult.status === "already" ? "bg-yellow-50 border border-yellow-200" :
            "bg-red-50 border border-red-200"
          }`}>
            {scanResult.status === "success" && <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />}
            {scanResult.status === "already" && <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />}
            {scanResult.status === "error" && <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />}
            <div>
              {scanResult.status === "success" && (
                <>
                  <p className="font-semibold text-green-800">✓ Checked in — {scanResult.name}</p>
                  {scanResult.ticketType && <p className="text-sm text-green-600">{scanResult.ticketType}</p>}
                </>
              )}
              {scanResult.status === "already" && (
                <p className="font-semibold text-yellow-800">Already checked in — {scanResult.name}</p>
              )}
              {scanResult.status === "error" && (
                <p className="font-semibold text-red-800">{scanResult.message}</p>
              )}
            </div>
            <button onClick={() => setScanResult(null)} className="ml-auto p-1 opacity-50 hover:opacity-100 transition">
              <XCircle className="w-4 h-4 text-grey-500" />
            </button>
          </div>
        )}

        {/* Scanner tab */}
        {activeTab === "scanner" && (
          <div className="space-y-4">

            {/* Camera view */}
            {cameraActive ? (
              <div className="bg-black rounded-2xl overflow-hidden relative">
                <video
                  ref={videoRef}
                  className="w-full aspect-square object-cover"
                  playsInline
                  muted
                />
                <canvas ref={canvasRef} className="hidden" />
                {/* Scan overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-56 h-56 border-2 border-white rounded-2xl opacity-60" />
                </div>
                {scanning && (
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                    <div className="bg-black/60 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2">
                      <Loader className="w-3 h-3 animate-spin" />
                      Checking in…
                    </div>
                  </div>
                )}
                <button
                  onClick={stopCamera}
                  className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 text-white rounded-xl transition"
                >
                  <CameraOff className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="bg-white border border-grey-200 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <QrCode className="w-5 h-5 text-grey-400" />
                  <div>
                    <h3 className="font-medium text-grey-900">QR Code Scanner</h3>
                    <p className="text-xs text-grey-500">Use camera or a USB QR scanner</p>
                  </div>
                </div>

                {/* Camera button */}
                <button
                  onClick={startCamera}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-grey-900 hover:bg-grey-800 text-white rounded-xl text-sm font-medium transition mb-4"
                >
                  <Camera className="w-4 h-4" />
                  Use Camera
                </button>

                {cameraError && (
                  <p className="text-xs text-red-500 mb-3">{cameraError}</p>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-grey-200" />
                  <span className="text-xs text-grey-400">or enter manually</span>
                  <div className="flex-1 h-px bg-grey-200" />
                </div>

                {/* USB / manual input */}
                <form onSubmit={handleQrSubmit}>
                  <div className="flex gap-2">
                    <input
                      ref={qrInputRef}
                      type="text"
                      value={qrInput}
                      onChange={e => setQrInput(e.target.value)}
                      placeholder="Paste ticket ID…"
                      className="flex-1 px-3.5 py-2.5 border border-grey-300 rounded-xl text-sm focus:ring-2 focus:ring-grey-900 focus:border-transparent font-mono"
                      autoComplete="off"
                    />
                    <button
                      type="submit"
                      disabled={!qrInput.trim() || scanning}
                      className="px-4 py-2.5 bg-grey-900 text-white rounded-xl text-sm font-medium disabled:opacity-50 transition flex items-center gap-2"
                    >
                      {scanning ? <Loader className="w-4 h-4 animate-spin" /> : "Check In"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Guest list tab */}
        {activeTab === "list" && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-grey-200 rounded-xl text-sm focus:ring-2 focus:ring-grey-900 focus:border-transparent"
              />
            </div>

            {attendees.length === 0 ? (
              <div className="bg-white border border-grey-200 rounded-2xl p-10 text-center">
                <Ticket className="w-10 h-10 text-grey-300 mx-auto mb-3" />
                <p className="text-grey-500 text-sm">No tickets sold yet.</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-grey-200 rounded-2xl p-8 text-center">
                <p className="text-grey-500 text-sm">No results for "{search}"</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map(attendee => (
                  <div
                    key={attendee.id}
                    className={`bg-white border rounded-2xl p-4 flex items-center gap-3 ${
                      attendee.checkedIn ? "border-green-200 bg-green-50" : "border-grey-200"
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      attendee.checkedIn ? "bg-green-100" : "bg-grey-100"
                    }`}>
                      {attendee.checkedIn
                        ? <CheckCircle className="w-4 h-4 text-green-600" />
                        : <span className="text-sm font-semibold text-grey-600">
                            {(attendee.buyerName || "?")[0].toUpperCase()}
                          </span>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-grey-900 text-sm truncate">{attendee.buyerName}</p>
                      <p className="text-xs text-grey-500 truncate">{attendee.buyerEmail}</p>
                      <p className="text-xs text-grey-400">{attendee.ticketTypeName}</p>
                    </div>
                    <div className="flex-shrink-0">
                      {attendee.checkedIn ? (
                        <span className="text-xs text-green-600 font-medium">Checked in</span>
                      ) : (
                        <button
                          onClick={() => handleListCheckIn(attendee.id)}
                          className="px-3 py-1.5 bg-grey-900 text-white text-xs rounded-lg hover:bg-grey-800 transition font-medium"
                        >
                          Check In
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckIn;
